import { Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { BookingDTO, ResourceApi, ResourceDTO } from '@lkp/parking-sdk';
import { RESOURCE_API } from '../../core/providers/api.module';
import { Subject, debounceTime, map, takeUntil } from 'rxjs';
import {
  BookingSearchListener,
  GetParkedCarBookings,
} from 'src/app/core/states/bookings/booking.actions';
import { BookingsState } from 'src/app/core/states/bookings/bookings.state';

@Component({
    selector: 'app-parked-cars',
    templateUrl: './parked-cars.component.html',
    styleUrls: ['./parked-cars.component.scss'],
    standalone: false
})
export class ParkedCarsComponent {
  bookings: BookingDTO[] = [];
  dispatchBookingsErrorCode?: any;
  totalBookings: number = 0;

  activeResourceId?: number;
  resources: ResourceDTO[] = [];

  pageIndex: number = 0;
  pageSize: number = 50;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  sortOrder?: 'asc' | 'desc';

  searchInput = new FormControl<string | undefined>(undefined);
  serviceTypeFilter = new FormControl<string | undefined>(undefined);

  showSpinner: boolean = false;

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly store: Store,
    actions$: Actions,
    @Inject(RESOURCE_API) readonly resourceApi: ResourceApi
  ) {
    actions$
      .pipe(
        ofActionDispatched(BookingSearchListener),
        map((action: BookingSearchListener) => action.term),
        debounceTime(300),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.showSpinner = true;
        this.getParkedCars();
      });
  }

  ngOnInit() {
    this.store
      .select(BookingsState.currentParkedCarBookings)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((v) => {
        this.bookings = v?.content ? v.content : [];
        this.totalBookings = v?.totalElements ? v.totalElements : 0;
      });

    /* Get resources */
    this.resourceApi
      .getResources()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data) => {
          this.resources = data;
          if (this.resources.length) {
            this.activeResourceId = this.resources[0].resourceId;
          }
          this.getParkedCars();
        },
        error: () => {
          console.error('Error fetching resources');
        },
      });

    /* Listen to input changes */
    this.searchInput.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.store.dispatch(
          new BookingSearchListener(this.searchInput.value || '')
        );
      });
    this.serviceTypeFilter.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.getParkedCars();
      });
  }

  paginator(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.getParkedCars();
  }

  getParkedCars() {
    const resource = this.resources.find(
      (r) => r.resourceId === this.activeResourceId
    );

    this.store
      .dispatch(
        new GetParkedCarBookings(
          this.pageIndex,
          this.pageSize,
          resource?.name || 'PARKING_OUTDOOR',
          undefined, // TODO: Fix sort, gives 500
          this.serviceTypeFilter.value || undefined,
          this.searchInput.value || undefined
        )
      )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => (this.showSpinner = false));
  }

  isGarage(): boolean {
    const resource = this.resources.find(
      (r) => r.resourceId === this.activeResourceId
    );
    return resource?.name === 'PARKING_GARAGE' || false;
  }

  activeResourceName(): string {
    return (
      this.resources.find((r) => r.resourceId === this.activeResourceId)
        ?.label || ''
    );
  }

  changeResourceType(value: number): void {
    this.pageIndex = 0;
    this.showSpinner = true;
    this.activeResourceId = +value;
    this.getParkedCars();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
